import React from "react";
import { Grid, TextField, MenuItem } from "@mui/material";
import { NumericFormat } from "react-number-format";
import { inputStyle } from "./styles";
import {
  TRUMID_RFQ_INQUIRY_TYPES,
  TRUMID_PRICE_TYPES,
  TRUMID_STIPULATION_TYPES,
  TRUMID_PARTIAL_EXECUTION,
} from "../../Constants/AppConstants";
import { normalizeYesNoToYN } from "./useOrderEntry";

const TruMidRfqFields = ({
  actualOrderData,
  handleChange,
  errors,
  isFieldEditable = () => true,
  clearFieldError,
  clearModalError,
  setActualOrderData, // Add this prop
  readOnly,
}) => {
  const handleTruMidChange = (event) => {
    const { name, value } = event.target;

    // if (readOnly || !isFieldEditable(name)) {
    //   return;
    // }

    // Clear field errors first
    if (value !== undefined && value !== "") {
      clearFieldError && clearFieldError(name);
      clearModalError && clearModalError(name);
    }

    // Handle special case for priceType change to PRICE
    if (name === "priceType" && value === TRUMID_PRICE_TYPES.PRICE) {
      // Use setActualOrderData directly to update both fields atomically
      setActualOrderData({
        ...actualOrderData,
        [name]: value,
        stipulationType: null,
      });
      console.log(
        `actualorderdata after priceType change:`,
        { ...actualOrderData, [name]: value, stipulationType: null }
      );
    } else {
      // For all other changes, use the regular handleChange
      handleChange(event);
    }
  };

  const showStipulationType =
    actualOrderData.priceType === TRUMID_PRICE_TYPES.SPREAD;
  const showTTL = actualOrderData.rfqInquiryType === TRUMID_RFQ_INQUIRY_TYPES.BIN;
  const normalizedAllowPartialExecution =
    normalizeYesNoToYN(actualOrderData.allowPartialExecution) ||
    TRUMID_PARTIAL_EXECUTION.YES;

  return (
    <>
      <Grid item xs={3}>
        <TextField
          fullWidth
          select
          name="rfqInquiryType"
          value={actualOrderData.rfqInquiryType || ""} // minutes stored in state elsewhere
          onChange={handleTruMidChange}
          variant="filled"
          label="RFQ Inquiry Type"
          error={errors?.rfqInquiryType}
          disabled={!isFieldEditable("rfqInquiryType")}
          sx={inputStyle}
          InputProps={{ disableUnderline: true }}
        >
          <MenuItem value={TRUMID_RFQ_INQUIRY_TYPES.ASAP}>
            {TRUMID_RFQ_INQUIRY_TYPES.ASAP}
          </MenuItem>
          <MenuItem value={TRUMID_RFQ_INQUIRY_TYPES.BIN}>
            {TRUMID_RFQ_INQUIRY_TYPES.BIN}
          </MenuItem>
        </TextField>
      </Grid>

      <Grid item xs={3}>
        <NumericFormat
          customInput={TextField}
          name="quoteSubmissionDeadline"
          value={actualOrderData.quoteSubmissionDeadline ?? ""}
          variant="filled"
          fullWidth
          label="Quote Deadline (minutes)"
          placeholder="Enter minutes (2-30)"
          allowNegative={false}
          decimalScale={0}
          disabled={!isFieldEditable("quoteSubmissionDeadline")}
          error={errors?.quoteSubmissionDeadline}
          sx={inputStyle}
          InputProps={{
            disableUnderline: true,
          }}
          isAllowed={(values) => {
            const { floatValue, value } = values;
            if (floatValue === undefined || value === "") return true;
            return Number.isInteger(floatValue) && floatValue <= 30;
          }}
          onValueChange={(values) => {
            const { floatValue, value } = values;
            handleTruMidChange({
              target: {
                name: "quoteSubmissionDeadline",
                value: value === "" ? "" : floatValue,
              },
            });
          }}
        />
      </Grid>

      <Grid item xs={3}>
        <TextField
          fullWidth
          select
          name="priceType"
          value={actualOrderData.priceType || ""} // no default, minutes logic centralized in useOrderEntry
          onChange={handleTruMidChange}
          variant="filled"
          label="Price Type"
          error={errors?.priceType}
          disabled={!isFieldEditable("priceType")}
          sx={inputStyle}
          InputProps={{ disableUnderline: true }}
        >
          <MenuItem value={TRUMID_PRICE_TYPES.PRICE}>
            {TRUMID_PRICE_TYPES.PRICE}
          </MenuItem>
          <MenuItem value={TRUMID_PRICE_TYPES.SPREAD}>
            {TRUMID_PRICE_TYPES.SPREAD}
          </MenuItem>
          {/* Note: Yield is only for MarketAxess, not TruMid RFQ */}
        </TextField>
      </Grid>

      {showStipulationType && (
        <Grid item xs={3}>
          <TextField
            fullWidth
            select
            name="stipulationType"
            value={actualOrderData.stipulationType || ""} // show empty if not provided
            onChange={handleTruMidChange}
            variant="filled"
            label="Stipulation Type"
            error={errors?.stipulationType}
            disabled={!isFieldEditable("stipulationType")}
            sx={inputStyle}
            InputProps={{ disableUnderline: true }}
          >
            <MenuItem value={TRUMID_STIPULATION_TYPES.HEDGE}>
              HEDGE
            </MenuItem>
            <MenuItem value={TRUMID_STIPULATION_TYPES.SPOT}>
              SPOT
            </MenuItem>
          </TextField>
        </Grid>
      )}

      <Grid item xs={3}>
        <TextField
          fullWidth
          select
          name="allowPartialExecution"
          value={normalizedAllowPartialExecution}
          onChange={handleTruMidChange}
          variant="filled"
          label="Allow Partial Executions"
          error={errors?.allowPartialExecution}
          disabled={!isFieldEditable("allowPartialExecution")}
          sx={inputStyle}
          InputProps={{ disableUnderline: true }}
        >
          <MenuItem value={TRUMID_PARTIAL_EXECUTION.YES}>Yes</MenuItem>
          <MenuItem value={TRUMID_PARTIAL_EXECUTION.NO}>No</MenuItem>
        </TextField>
      </Grid>

      {/* TTL field for BIN inquiry type */}
      {showTTL && (
        <Grid item xs={3}>
          <NumericFormat
            customInput={TextField}
            name="ttl"
            value={actualOrderData.ttl ?? ""}
            variant="filled"
            fullWidth
            label="Quote Firm Time (minutes)"
            placeholder="Enter minutes (2-30)"
            allowNegative={false}
            decimalScale={0}
            disabled={!isFieldEditable("ttl")}
            error={errors?.ttl}
            sx={inputStyle}
            InputProps={{
              disableUnderline: true,
            }}
            isAllowed={(values) => {
              const { floatValue, value } = values;
              if (floatValue === undefined || value === "") return true;
              return Number.isInteger(floatValue) && floatValue <= 30;
            }}
            onValueChange={(values) => {
              const { floatValue, value } = values;
              handleTruMidChange({
                target: {
                  name: "ttl",
                  value: value === "" ? "" : floatValue,
                },
              });
            }}
          />
        </Grid>
      )}
    </>
  );
};

export default TruMidRfqFields;
